import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrders } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { FaCheckCircle, FaClock, FaTruck } from 'react-icons/fa';

const Orders = () => {
  const location = useLocation();
  const { getUserOrders } = useOrders();
  const { user } = useAuth();
  
  const orders = user ? getUserOrders(user.id) : [];

  useEffect(() => {
    if (location.state?.message) {
      // Show success message if coming from checkout
      setTimeout(() => {
        // Clear the message after showing
        window.history.replaceState({}, document.title);
      }, 5000);
    }
  }, [location.state]);

  if (!user) {
    return (
      <div className="container text-center mt-5">
        <h2>Please login to view your orders</h2>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FaCheckCircle className="text-success" />;
      case 'pending':
        return <FaClock className="text-warning" />;
      default:
        return <FaTruck className="text-info" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container">
      <h2 className="text-center mb-4">My Orders</h2>
      
      {location.state?.message && (
        <div className="alert alert-success text-center">
          {location.state.message}
          {location.state.orderId && (
            <div className="mt-2">
              <strong>Order ID: {location.state.orderId}</strong>
            </div>
          )}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="empty-orders-state">
          <div className="empty-orders-illustration">
            <div className="illustration-container">
              <div className="food-illustration">🍽️</div>
              <div className="order-illustration">📋</div>
              <div className="delivery-illustration">🚚</div>
            </div>
          </div>
          
          <div className="empty-orders-content">
            <h2 className="empty-orders-title">No Orders Yet</h2>
            <p className="empty-orders-description">
              Ready to start your delicious food journey? Place your first pre-order and join our community of food lovers!
            </p>
            
            <div className="empty-orders-features">
              <div className="feature-item">
                <div className="feature-icon">🌟</div>
                <div className="feature-text">
                  <strong>Fresh Homemade Food</strong>
                  <span>Delicious meals prepared with care</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📅</div>
                <div className="feature-text">
                  <strong>Convenient Pre-Ordering</strong>
                  <span>Order ahead for batch delivery</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🏘️</div>
                <div className="feature-text">
                  <strong>Society Delivery</strong>
                  <span>Delivered right to your society</span>
                </div>
              </div>
            </div>
            
            <div className="empty-orders-actions">
              <a href="/products" className="btn btn-primary btn-lg">
                Browse Menu
              </a>
              <a href="/" className="btn btn-outline-primary btn-lg">
                Learn More
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {orders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div>
                  <span className="order-id" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%' }}>
                    <strong>Order ID:</strong> {(() => {
                      const date = new Date(order.createdAt);
                      const pad = n => n.toString().padStart(2, '0');
                      const y = date.getFullYear();
                      const m = pad(date.getMonth() + 1);
                      const d = pad(date.getDate());
                      const h = pad(date.getHours());
                      const min = pad(date.getMinutes());
                      const suffix = order._id.slice(-4);
                      return `${y}${m}${d}-${h}${min}-${suffix}`;
                    })()}
                  </span>
                  <div className="text-muted">
                    {formatDate(order.createdAt)}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {getStatusIcon(order.status)}
                  <span className={`order-status ${order.status}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>
              </div>

              <div className="order-items">
                {order.items.map((item) => (
                  <div key={item._id} className="order-item">
                    <span>{item.productName} x {item.quantity}</span>
                    <span>₹{item.totalPrice.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="order-item">
                <span>Subtotal:</span>
                                  <span>₹{order.subtotal.toFixed(2)}</span>
              </div>
              
              <div className="order-item">
                <span>Delivery Fee:</span>
                                  <span>₹{order.deliveryFee.toFixed(2)}</span>
              </div>

              <div className="order-item">
                <strong>Total:</strong>
                <strong className="order-total">₹{order.total.toFixed(2)}</strong>
              </div>

              <div className="mt-3">
                <h6>Delivery Information:</h6>
                <p className="mb-1"><strong>Name:</strong> {order.userName}</p>
                <p className="mb-1"><strong>Phone:</strong> {order.userPhone}</p>
                <p className="mb-1"><strong>Society:</strong> {order.userSociety}</p>
                <p className="mb-0"><strong>Address:</strong> {order.userAddress}</p>
              </div>

              {order.status === 'pending' && (
                <div className="mt-3 p-3 bg-light rounded">
                  <p className="mb-0 text-muted">
                    <FaClock className="me-2" />
                    Your order is being prepared. We'll notify you when it's ready for delivery!
                  </p>
                </div>
              )}

              {order.status === 'completed' && (
                <div className="mt-3 p-3 bg-success bg-opacity-10 rounded">
                  <p className="mb-0 text-success">
                    <FaCheckCircle className="me-2" />
                    Order completed! Thank you for choosing our service.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders; 